// Shared YouTube-player helpers.
// ================================
// Pure, side-effect-free utilities that were previously copy-pasted
// verbatim across AudioVisualizerPlayer, InAppVideoPlayer,
// InAppMoviePlayer and MiniPlayer. Centralised here so the embed-URL
// shape and the duration parsers have ONE source of truth (they used to
// drift between files, which caused subtle continuous-play bugs).
//
// NOTE: The stateful postMessage listeners / unmute schedules stay
// inside each player because they close over per-track flags
// (`unmuted`, wall-clock baselines, near-end debounce). Only the pure
// pieces live here.

// Safely parse a JSON string coming from a YouTube postMessage event.
export function safeJSON(s) {
  try { return JSON.parse(s); } catch { return null; }
}

// Parse a catalog duration ("4 min", "3:42", "1:23:45", "240") into ms.
// Returns 0 when unparseable. Drives the continuous-play catalog timer.
export function parseDurationToMs(raw) {
  if (!raw) return 0;
  const s = String(raw).trim().toLowerCase();
  const minMatch = s.match(/(\d+)\s*(?:min|m)/);
  const secMatch = s.match(/(\d+)\s*(?:sec|s)/);
  if (minMatch) {
    const m = parseInt(minMatch[1], 10) || 0;
    const sec = secMatch ? parseInt(secMatch[1], 10) || 0 : 0;
    return (m * 60 + sec) * 1000;
  }
  const parts = s.split(":").map((p) => parseInt(p, 10));
  if (parts.every((n) => Number.isFinite(n))) {
    if (parts.length === 2) return (parts[0] * 60 + parts[1]) * 1000;
    if (parts.length === 3) return (parts[0] * 3600 + parts[1] * 60 + parts[2]) * 1000;
  }
  const n = parseInt(s, 10);
  if (Number.isFinite(n) && n > 0) return n * 1000;
  return 0;
}

// Parse a human movie runtime ("2 h 9 min", "97 min", "1 h") to seconds.
// Used to spot when a "full movie" YouTube embed is actually a trailer.
export function parseRuntimeToSeconds(d) {
  if (!d || typeof d !== "string") return 0;
  const s = d.toLowerCase();
  const hMatch = s.match(/(\d+)\s*h/);
  const mMatch = s.match(/(\d+)\s*m(?:in)?/);
  const hr = hMatch ? parseInt(hMatch[1], 10) : 0;
  const mn = mMatch ? parseInt(mMatch[1], 10) : 0;
  if (!hr && !mn) {
    const n = s.match(/(\d+)/);
    if (n) return parseInt(n[1], 10) * 60;
    return 0;
  }
  return hr * 3600 + mn * 60;
}

// Build the in-app YouTube embed URL. `mute` accepts 0/1 (or boolean).
// Keep the query-string shape identical to what every player has always
// used — muted-autoplay is the cross-browser-safe default and the
// players unmute via the IFrame API after playback begins.
export function buildYouTubeEmbedSrc(youtubeId, { mute = 1, ccOn = false, origin = "", start = 0 } = {}) {
  const m = mute === true ? 1 : mute === false ? 0 : mute;
  return `https://www.youtube.com/embed/${youtubeId}?autoplay=1&mute=${m}&rel=0&playsinline=1&modestbranding=1&enablejsapi=1&start=${start}${ccOn ? "&cc_load_policy=1&cc_lang_pref=en" : ""}&origin=${encodeURIComponent(origin)}`;
}
